import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, Heart, Users, ArrowRight, GraduationCap, Laptop, UserCheck, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

export default function EducationPage() {
  return (
    <div data-testid="education-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-white/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Education & Lifelong Learning
            </h1>
            <p className="text-xl text-slate-800">
              Expanding access to quality education and inclusive learning opportunities
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Images */}
      <section className="relative py-12 overflow-hidden bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_fa85df46-347d-469f-8cb9-d6bc46d2cb89/artifacts/u0lipm76_12d8f04c979147e4ad6c52b5e30cd67f.jpg"
                alt="OPAD Education - Vocational Skills Training"
                className="w-full h-72 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">Vocational tailoring skills training programme</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_fa85df46-347d-469f-8cb9-d6bc46d2cb89/artifacts/1wxqqspb_world%20skills%20day.jpg.webp"
                alt="OPAD Education - Digital Skills Training"
                className="w-full h-72 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">Digital literacy and computer skills training</p>
            </motion.div>
          </div>
          <p className="text-center text-slate-700 mt-8 max-w-2xl mx-auto">
            Empowering communities through quality education and skills development programmes
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#7BC4E2]/10 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#E08B7A]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">About Education & Lifelong Learning</h2>
              <div className="w-24 h-1 bg-[#7BC4E2] mx-auto rounded-full"></div>
            </div>
            
            <div className="bg-white/90 backdrop-blur-sm p-8 md:p-12 rounded-3xl shadow-lg border border-slate-100">
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                At the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong>, our Education and Lifelong Learning programmes are designed to expand access to quality education, enhance skills development, and promote inclusive learning opportunities for individuals of all ages.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Recognizing education as a fundamental driver of sustainable development and poverty reduction, OPAD works collaboratively with communities, institutions, and development partners to strengthen learning systems that empower individuals with the knowledge and competencies needed for personal and professional growth.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Our initiatives support <strong className="text-[#7BC4E2]">formal and non-formal education pathways</strong>, vocational training, digital literacy programmes, and leadership development activities that improve employability and social inclusion. We focus on equipping youth and adults with practical skills that enable them to participate meaningfully in economic and community development processes.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Special emphasis is placed on <strong className="text-[#E08B7A]">marginalized groups, including women and disadvantaged youth</strong>, to ensure equitable access to educational opportunities and lifelong learning resources.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg">
                Through innovative learning solutions, community engagement, and strategic partnerships, OPAD fosters environments where individuals can develop critical thinking, technical skills, and entrepreneurial capacities that contribute to sustainable livelihoods and community resilience. By promoting continuous learning and capacity building, our programmes contribute to advancing global development priorities and supporting inclusive growth for present and future generations.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#7BC4E2]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#E5B75F]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">What We Do</h2>
            <div className="w-24 h-1 bg-[#7BC4E2] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#7BC4E2]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 bg-[#7BC4E2]/10 rounded-xl flex items-center justify-center mb-4">
                <GraduationCap className="w-7 h-7 text-[#7BC4E2]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Skills Development & Vocational Training</h3>
              <p className="text-slate-600">Providing practical skills training to enhance employability and entrepreneurship opportunities.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#E08B7A]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 bg-[#E08B7A]/10 rounded-xl flex items-center justify-center mb-4">
                <Laptop className="w-7 h-7 text-[#E08B7A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Digital Literacy & Innovation</h3>
              <p className="text-slate-600">Promoting access to digital skills and technology-driven learning opportunities.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#4A7C59]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center mb-4">
                <UserCheck className="w-7 h-7 text-[#4A7C59]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Inclusive Education Support</h3>
              <p className="text-slate-600">Supporting equitable access to education for marginalized and disadvantaged groups.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OPAD Focus Areas */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#E5B75F]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#7BC4E2]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
                OPAD Focus Areas
              </h2>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Youth Skills Development</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Adult Learning & Capacity Building</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Digital Inclusion & Innovation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E5B75F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Gender Equality in Education</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Entrepreneurship & Leadership Development</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Education and Learning"
                className="rounded-3xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[350px] h-[300px] bg-white/10 rounded-bl-[150px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[120px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Lightbulb className="w-16 h-16 text-slate-900 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Need Help? Get Involved Today
            </h2>
            <p className="text-slate-800 text-lg mb-8">
              Support our education initiatives and help transform lives through learning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-8 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/get-involved">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer With Us
                </Button>
              </Link>
              <Link to="/programs">
                <Button variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6 text-lg font-semibold">
                  View All Programs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
